// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { authAPI } from "../services/api/auth.api";
import { AUTH_CONSTANTS } from "../constants";
import ChangePasswordModal from "../components/features/auth/ChangePasswordModal";

// Create the AuthContext
export const AuthContext = createContext(null);

// AuthProvider component
export const AuthProvider = ({ children }) => {
  console.log("🚀 AuthProvider is mounting...");

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);

  console.log("🚀 AuthProvider state initialized");

  // Check if user is logged in on app start
  useEffect(() => {
    console.log("🚀 AuthProvider useEffect is running - checking auth status");
    checkAuthStatus();
  }, []);

  // Listen for storage changes (when Redux updates localStorage)
  useEffect(() => {
    const handleStorageChange = () => {
      console.log("🚀 localStorage changed, rechecking auth status");
      checkAuthStatus();
    };

    // Listen to custom storage event
    window.addEventListener("storage", handleStorageChange);

    // Also check periodically for changes (since same-window storage events don't fire)
    const interval = setInterval(() => {
      const userData = localStorage.getItem(AUTH_CONSTANTS.USER_KEY);
      if (userData) {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.is_first_login && !showFirstLoginModal) {
          console.log(
            "🚀 Detected is_first_login=true from periodic check, showing modal"
          );
          setShowFirstLoginModal(true);
        } else if (!parsedUser.is_first_login && showFirstLoginModal) {
          console.log(
            "🚀 Detected is_first_login=false from periodic check, hiding modal"
          );
          setShowFirstLoginModal(false);
        }
      }
    }, 1000); // Check every second

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [showFirstLoginModal]);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem(AUTH_CONSTANTS.TOKEN_KEY);
      const userData = localStorage.getItem(AUTH_CONSTANTS.USER_KEY);

      console.log("Checking auth status...");
      console.log("Token exists:", !!token);
      console.log("User data exists:", !!userData);

      if (token && userData) {
        const parsedUser = JSON.parse(userData);
        console.log("Parsed user from localStorage:", parsedUser);
        console.log("Stored is_first_login:", parsedUser.is_first_login);

        setUser(parsedUser);
        setIsAuthenticated(true);

        // Check if user needs to change password on first login from stored data
        if (parsedUser.is_first_login) {
          console.log(
            "User has is_first_login=true from localStorage, showing modal immediately"
          );
          setShowFirstLoginModal(true);
        }

        // Optionally verify token with backend and refresh user data
        await fetchUserProfile();
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      if (response.success) {
        const updatedUser = response.data;
        console.log("User Profile Fetched:", updatedUser);
        console.log("is_first_login status:", updatedUser.is_first_login);

        setUser(updatedUser);
        localStorage.setItem(
          AUTH_CONSTANTS.USER_KEY,
          JSON.stringify(updatedUser)
        );

        // Check if user needs to change password on first login
        if (updatedUser.is_first_login) {
          console.log("Setting showFirstLoginModal to TRUE");
          setShowFirstLoginModal(true);
        } else {
          console.log("is_first_login is false, hiding modal if it was open");
          setShowFirstLoginModal(false);
        }
      }
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      // Don't logout on profile fetch failure, token might still be valid
    }
  };

  const login = async (credentials) => {
    try {
      setLoading(true);
      const response = await authAPI.login(credentials);

      if (response.success) {
        const {
          token,
          refreshToken,
          user: userData,
          requirePasswordChange,
          isFirstLogin,
        } = response.data;

        // Store tokens and user data
        localStorage.setItem(AUTH_CONSTANTS.TOKEN_KEY, token);
        if (refreshToken) {
          localStorage.setItem(AUTH_CONSTANTS.REFRESH_TOKEN_KEY, refreshToken);
        }
        localStorage.setItem(AUTH_CONSTANTS.USER_KEY, JSON.stringify(userData));

        setUser(userData);
        setIsAuthenticated(true);

        // Check if user needs to change password on first login
        if (isFirstLogin || userData.is_first_login) {
          setShowFirstLoginModal(true);
        }

        return {
          success: true,
          data: userData,
          requirePasswordChange: requirePasswordChange || false,
          isFirstLogin: isFirstLogin || false,
        };
      } else {
        throw new Error(response.message || "Login failed");
      }
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        message: error.message || "Login failed. Please try again.",
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Clear stored data
    localStorage.removeItem(AUTH_CONSTANTS.TOKEN_KEY);
    localStorage.removeItem(AUTH_CONSTANTS.REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_CONSTANTS.USER_KEY);

    // Reset state
    setUser(null);
    setIsAuthenticated(false);

    // Redirect to login page
    window.location.href = AUTH_CONSTANTS.LOGOUT_REDIRECT;
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem(AUTH_CONSTANTS.USER_KEY, JSON.stringify(userData));
  };

  const handleFirstLoginPasswordChange = async () => {
    console.log(
      "Password changed successfully, hiding modal and refreshing user data"
    );
    setShowFirstLoginModal(false);

    // Refresh user profile to get updated is_first_login status from backend
    await fetchUserProfile();

    // Also update localStorage immediately to prevent modal from reappearing
    const userData = localStorage.getItem(AUTH_CONSTANTS.USER_KEY);
    if (userData) {
      const parsedUser = JSON.parse(userData);
      parsedUser.is_first_login = false;
      localStorage.setItem(AUTH_CONSTANTS.USER_KEY, JSON.stringify(parsedUser));
      console.log("Updated localStorage to set is_first_login to false");
    }
  };

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem(
        AUTH_CONSTANTS.REFRESH_TOKEN_KEY
      );
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await authAPI.refreshToken(refreshToken);

      if (response.success) {
        const { token, refreshToken: newRefreshToken } = response.data;

        localStorage.setItem(AUTH_CONSTANTS.TOKEN_KEY, token);
        if (newRefreshToken) {
          localStorage.setItem(
            AUTH_CONSTANTS.REFRESH_TOKEN_KEY,
            newRefreshToken
          );
        }

        return token;
      } else {
        throw new Error("Token refresh failed");
      }
    } catch (error) {
      console.error("Token refresh error:", error);
      logout();
      throw error;
    }
  };

  const contextValue = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    refreshToken,
    checkAuthStatus,
    showFirstLoginModal,
    handleFirstLoginPasswordChange,
  };

  // Debug logging for modal state
  console.log("AuthContext render - showFirstLoginModal:", showFirstLoginModal);
  console.log("AuthContext render - user:", user);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <ChangePasswordModal
        open={showFirstLoginModal}
        onClose={() => setShowFirstLoginModal(false)}
        onSuccess={handleFirstLoginPasswordChange}
        userEmail={user?.email}
        isFirstLogin={true}
      />
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
